<template>
  <div>
    <!-- In server mode the database does the paging, filtering and sorting;
         server-items-length is what tells v-data-table to stop doing all three
         to the page it has just been given. -->
    <v-data-table
        v-model="selected"
        :show-select="tab < 3"
        :options.sync="tableOptions"
        :footer-props="footerProps"
        :no-data-text="$t('noData')"
        :no-results-text="$t('noResults')"
        item-key="id"
        :loading="isLoading"
        loading-text="Loading... Please wait"
        :headers="getHeader()"
        :items="users"
        :search="serverMode ? '' : debouncedSearch"
        :server-items-length="serverMode ? total : -1"
      >
        <template v-slot:top>
          <bulk-action-bar
            :count="selected.length"
            :running="bulkRunning"
            :done="bulkDone"
            :total="bulkTotal"
            :progress="bulkProgress"
            @clear="clearSelection"
          >
            <!-- the review queue takes a decision, not a status change -->
            <template v-if="tab === 2">
              <v-btn text small @click="bulkTakeAction(1)">
                <v-icon small class="me-1">mdi-check</v-icon>
                {{ $t('approve_selected') }}
              </v-btn>
              <v-btn text small @click="bulkTakeAction(2)">
                <v-icon small class="me-1">mdi-close</v-icon>
                {{ $t('reject_selected') }}
              </v-btn>
            </template>
            <template v-else>
              <v-btn text small @click="bulkSetStatus(3)">
                <v-icon small class="me-1">mdi-account-off</v-icon>
                {{ $t('suspend_selected') }}
              </v-btn>
              <v-btn text small @click="bulkSetStatus(1)">
                <v-icon small class="me-1">mdi-account-check</v-icon>
                {{ $t('activate_selected') }}
              </v-btn>
              <v-btn
                v-if="userType === 'students'"
                text
                small
                @click="$emit('map-students', selected)"
              >
                <v-icon small class="me-1">mdi-map-marker-path</v-icon>
                {{ $t('map_to_route') }}
              </v-btn>
              <!-- taking a class off a route at the end of term was three
                   clicks per child through the mapping dialog -->
              <v-btn
                v-if="userType === 'students'"
                text
                small
                @click="$emit('unmap-students', selected)"
              >
                <v-icon small class="me-1">mdi-map-marker-off</v-icon>
                {{ $t('unmap') }}
              </v-btn>
            </template>
            <v-btn v-if="userType === 'schools'" text small @click="bulkDeleteSchools">
              <v-icon small class="me-1">mdi-delete</v-icon>
              {{ $t('delete_selected') }}
            </v-btn>
            <v-btn v-else text small @click="bulkDeleteUsers">
              <v-icon small class="me-1">mdi-delete</v-icon>
              {{ $t('delete_selected') }}
            </v-btn>
          </bulk-action-bar>
          <v-text-field
            v-model="search"
            :label="$t('Search')"
            class="mx-4"
          ></v-text-field>
        </template>

        <template v-slot:item.status_id="{ item }">
          <v-chip
            :color="getStatusColor(item.status_id)"
            dark
          >
            {{ getStatusValue(item.status_id) }}
          </v-chip>
        </template>

        <template v-slot:item.name="{ item }">
            {{item.name}}
            <v-tooltip v-if="item.expired != null && item.expired"  bottom color="info">
                <template v-slot:activator="{ on }">
                <v-icon color="error" v-on="on">
                    mdi-cancel
                </v-icon>
                </template>
                <span>{{ $t('driver_documents_expired') }}</span>
            </v-tooltip>
        </template>
        <template v-if="userType === 'guardians'" v-slot:item.role_id="{ item }">
                    <v-chip
                        dark
                        :color="item.role_id === 4? 'primary' : 'info'"
                    >
                        {{ item.role_id === 4? $t('parent') : $t('guardian') }}
                    </v-chip>
        </template>
        <template v-if="userType === 'drivers'" v-slot:item.bus="{ item }">
          <!-- a driver may hold several buses now, so list them all -->
          <span v-if="item.buses && item.buses.length">
            <v-chip
              v-for="bus in item.buses"
              :key="bus.id"
              class="ma-1"
              dark
              @click="$emit('assignBus', item)"
              :color="getBusAssignmentColor(bus)"
            >
              {{ bus.license }}
            </v-chip>
          </span>
          <v-chip
            v-else
            dark
            @click="$emit('assignBus', item)"
            :color="getBusAssignmentColor(item.bus)"
          >
            {{ item.bus != null? item.bus.license : $t('no_bus') }}
          </v-chip>
        </template>

        <template v-slot:item.balance="{ item }">
          <v-chip
            small
            dark
            :color="item.balance > 0 ? 'success' : 'error'"
            @click="$emit('add-coins', item)"
          >
            {{ item.balance || 0 }}
          </v-chip>
        </template>

        <!-- half a mapping is not none of it: a child with a pickup but no
             drop-off was labelled exactly like a child with nothing at all,
             so a mapping that had just been saved read as though it had not -->
        <template v-slot:item.mapping="{ item }">
          <v-chip
            x-small
            dark
            :color="mappingColor(item)"
            @click="$emit('map-students', [item])"
          >
            {{ mappingLabel(item) }}
          </v-chip>
        </template>

        <template v-slot:item.created_at="{ item }">
          <small>{{ item.created_at | moment("LL") }}</small> -
          <small class="text-muted">{{ item.created_at | moment("LT") }}</small>
        </template>
        <!-- a student only gets a settings row once they are mapped to a bus,
             a stop or a trip, so a child added from the panel has none at all
             and reading through it broke the whole list -->
        <template v-slot:item.student_settings.morning_bus="{ item }">
          <v-tooltip v-if="tab<2 && userType === 'students' && mode === 'simple'" bottom>
            <template v-slot:activator="{ on, attrs }">
                <v-chip
                    dark
                    @click="$emit('set-student-bus', item, 1)"
                    :color="getBusAssignmentColor(studentBus(item, 1))"
                >
                    {{ studentBus(item, 1) ? studentBus(item, 1).license : $t('no_bus') }}
                </v-chip>
            </template>
            <span>{{ $t('set_morning_bus') }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.student_settings.afternoon_bus="{ item }">
          <v-tooltip v-if="tab<2 && userType === 'students' && mode === 'simple'" bottom>
            <template v-slot:activator="{ on, attrs }">
                <v-chip
                    dark
                    @click="$emit('set-student-bus', item, 0)"
                    :color="getBusAssignmentColor(studentBus(item, 0))"
                >
                    {{ studentBus(item, 0) ? studentBus(item, 0).license : $t('no_bus') }}
                </v-chip>
            </template>
            <span>{{ $t('set_afternoon_bus') }}</span>
          </v-tooltip>
        </template>


        <template v-slot:item.actions="{ item }">
          <v-tooltip v-if="tab==2" :key="'take-action-'+tab" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                small
                class="mx-2 primary"
                @click="$emit('view-user', item)"
              >
                <v-icon small class="mx-2">
                  mdi-card-account-mail-outline
                </v-icon>
                {{ $t('Take Action') }}
              </v-btn>
            </template>
            <span>{{ $t('approve_or_reject') }}</span>
          </v-tooltip>
          <!-- deciding on the spot: opening each person to press one of two
               buttons was the whole of the review screen for most rows -->
          <v-tooltip v-if="tab==2" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" color="success" @click="$emit('approve-user', item)">
                mdi-check-circle-outline
              </v-icon>
            </template>
            <span>{{ $t('approve') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab==2" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" color="error" @click="$emit('reject-user', item)">
                mdi-close-circle-outline
              </v-icon>
            </template>
            <span>{{ $t('reject') }}</span>
          </v-tooltip>
          <v-tooltip v-else :key="'eye-'+tab" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('view-user', item)">
                mdi-eye
              </v-icon>
            </template>
            <span>{{ $t('view') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab==0" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-if="checkIfLocationEnabled(item) && mode === 'simple'" v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('show-student-location', item)">
                mdi-map-marker
              </v-icon>
            </template>
            <span>{{ $t('pickup_and_drop_off_locations') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab<2"  bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('edit-user', item)">
                mdi-pencil
              </v-icon>
            </template>
            <span>{{ $t('edit') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab<2" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('suspend-user', item, users.indexOf(item))">
               {{item.status_id==3? icons.mdiAccountCheck: icons.mdiAccountOff}}
              </v-icon>
            </template>
            <span>{{item.status_id==3? $t('activate_user') : $t('suspend_user') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab<2 && userType === 'students'" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('map-students', [item])">
                mdi-map-marker-path
              </v-icon>
            </template>
            <span>{{ $t('map_to_route') }}</span>
          </v-tooltip>
          <!-- set the password here and tell them what it is: the emailed link
               never arrived for anyone whose address was mistyped, and it was
               the only way back into an account -->
          <v-tooltip v-if="tab<2 && canResetPassword(item)" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('set-password', item)">
                mdi-lock-reset
              </v-icon>
            </template>
            <span>{{ $t('set_password') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab!=2" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-if="userType === 'drivers' && item.bus" v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('unassign-bus', item)">
                mdi-fridge-industrial-off
              </v-icon>
              <v-icon v-else-if="userType === 'drivers'" v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('assign-bus', item)">
                mdi-bus
              </v-icon>
            </template>
            <span>{{item.bus? $t('unassign_bus_title') : $t('assign_bus') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab!=2 && userType === 'schools'" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('add-coins', item)">
                mdi-hand-coin
              </v-icon>
            </template>
            <span>{{ $t('add_coins') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab!=2" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-if="userType === 'schools'" v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('login-as-school', item)">
                mdi-login
              </v-icon>
            </template>
            <span>{{ $t('login_as_this_school') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab!=2" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-if="userType === 'schools'" v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('delete-school', item)">
                mdi-delete
              </v-icon>
            </template>
            <span>{{ $t('delete_this_school') }}</span>
          </v-tooltip>
          <!-- a duplicate or a mistyped row could only ever be suspended
               before, so it stayed in the list and still counted -->
          <v-tooltip v-if="userType !== 'schools'" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" color="error" @click="$emit('delete-user', item)">
                mdi-delete
              </v-icon>
            </template>
            <span>{{ $t('delete_user') }}</span>
          </v-tooltip>

        </template>
    </v-data-table>
    <bulk-result-dialog
      v-model="bulkResultDialog"
      :title="bulkResultTitle"
      :failures="bulkFailures"
    ></bulk-result-dialog>
    <confirm-notify-dialog
      v-model="bulkStatusDialog"
      :title="pendingBulkStatus === 3 ? $t('suspend_selected') : $t('activate_selected')"
      :text="pendingBulkStatus === 3
        ? $t('confirm_bulk_suspend', { count: selected.length })
        : $t('confirm_bulk_activate', { count: selected.length })"
      :confirm-text="$t('yes')"
      icon="mdi-account-multiple"
      @confirm="onBulkStatusConfirmed"
    ></confirm-notify-dialog>
  </div>
</template>

<script>
import {
  mdiAccountCheck,
  mdiAccountOff,
} from "@mdi/js";

import tableState from "@/mixins/tableState";
import studentMapping from "@/mixins/studentMapping";
import bulkActions from "@/mixins/bulkActions";
import BulkActionBar from "@/components/BulkActionBar";
import BulkResultDialog from "@/components/BulkResultDialog";
import ConfirmNotifyDialog from "@/components/ConfirmNotifyDialog";

export default {
  mixins: [tableState, bulkActions, studentMapping],
  props: {
    users: Array,
    userType: String,
    tab: Number,
    mode: String,
    //server mode: the parent fetches a page at a time and passes the total in
    serverMode: { type: Boolean, default: false },
    total: { type: Number, default: 0 },
    isLoading: { type: Boolean, default: false },
  },
  components: { BulkActionBar, BulkResultDialog, ConfirmNotifyDialog },
  data() {
    return {
      tableStateKey: "users",
      bulkStatusDialog: false,
      pendingBulkStatus: 1,
      defaultsHeaders: [
        { text: "id", value: "id", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "Email", value: "email" },
        { text: "Status", value: "status_id" },
        { text: "Created", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      schoolHeaders: [
        { text: "id", value: "id", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "Email", value: "email" },
        { text: "coins", value: "balance" },
        { text: "Status", value: "status_id" },
        { text: "Created", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      driverHeaders: [
        { text: "id", value: "id", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "Email", value: "email" },
        { text: "Bus", value: "bus" },
        { text: "Status", value: "status_id" },
        { text: "Created", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      studentAdvancedHeaders: [
        { text: "id", value: "student_identification", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "Parent Name", value: "parent.name" },
        { text: "Parent Email", value: "parent.email" },
        { text: "route", value: "mapping", sortable: false },
        { text: "Status", value: "status_id" },
        { text: "Created", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      studentSimpleHeaders: [
        { text: "id", value: "student_identification", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "Parent Name", value: "parent.name" },
        { text: "Parent Email", value: "parent.email" },
        //morning bus
        { text: "Morning Bus", value: "student_settings.morning_bus" },
        //afternoon bus
        { text: "Afternoon Bus", value: "student_settings.afternoon_bus" },
        { text: "Status", value: "status_id" },
        { text: "Created", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      parentHeaders: [
        { text: "id", value: "id", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "Email", value: "email" },
        { text: "Role", value: "role_id" },
        { text: "Status", value: "status_id" },
        { text: "Created", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      statuses: [
        { value: "Active", color: "success" },
        { value: "Pending", color: "warning" },
        { value: "Suspended", color: "error" },
        { value: "Under Review", color: "error" },
        { value: "Out of Coins", color: "error" },
      ],
      icons: {
        mdiAccountCheck,
        mdiAccountOff,
      },
    };
  },
  watch: {
    //server mode: every change of page, size or sort is a new question for the
    //database, and the search is answered there too
    tableOptions: {
      deep: true,
      handler() {
        if (this.serverMode) this.requestPage();
      },
    },
    debouncedSearch() {
      if (this.serverMode) this.requestPage();
    },
    tab() {
      //a different status: start at the top of it
      this.clearSelection();
    },
  },
  mounted() {
    if (this.serverMode) this.requestPage();
  },
  computed: {
    translatedDefaultsHeaders() {
      return this.defaultsHeaders.map(header => ({
        ...header,
        text: this.$t(header.text)
      }));
    },
    translatedDriverHeaders() {
      return this.driverHeaders.map(header => ({
        ...header,
        text: this.$t(header.text.toLowerCase().replace(' ', '_'))
      }));
    },
    translatedStudentAdvancedHeaders() {
      return this.studentAdvancedHeaders.map(header => ({
        ...header,
        text: this.$t(header.text.replace(' ', '_'))
      }));
    },
    translatedStudentSimpleHeaders() {
      return this.studentSimpleHeaders.map(header => ({
        ...header,
        text: this.$t(header.text.replace(' ', '_').replace('.', '_'))
      }));
    },
    translatedParentHeaders() {
      return this.parentHeaders.map(header => ({
        ...header,
        text: this.$t(header.text)
      }));
    },
    translatedSchoolHeaders() {
      return this.schoolHeaders.map(header => ({
        ...header,
        text: this.$t(header.text)
      }));
    },
    translatedStatuses() {
      return this.statuses.map(status => ({
        ...status,
        value: this.$t(status.value.toLowerCase().replace(' ', '_'))
      }));
    },
  },
  methods: {
    requestPage() {
      const options = this.tableOptions || {};
      this.$emit("fetch", {
        page: options.page || 1,
        //"All" is -1 in the footer; the endpoint is capped at 500 either way
        per_page: options.itemsPerPage > 0 ? options.itemsPerPage : 500,
        sort_by: (options.sortBy || [])[0] || null,
        sort_desc: (options.sortDesc || [])[0] ? 1 : 0,
        search: this.debouncedSearch || null,
      });
    },
    //only real database columns can be ordered server side; the rest say so
    //rather than sorting the page in the browser and looking like they worked
    applySortability(headers) {
      if (!this.serverMode) return headers;

      const sortable = ["id", "name", "email", "created_at", "status_id", "student_identification", "balance"];
      return headers.map((header) => ({
        ...header,
        sortable: sortable.indexOf(header.value) !== -1,
      }));
    },
    // Sends the status wanted, not a toggle. The old endpoint flipped whatever
    // it found, so a bulk "suspend" reactivated everyone in the selection who
    // was already suspended; the panel avoided that by filtering the selection
    // against its own copy of the list, which is only as fresh as the last load.
    bulkSetStatus(targetStatus) {
      if (!this.selected.length) return;
      //asks to confirm and to notify in one dialog, like the single row action
      this.pendingBulkStatus = targetStatus;
      this.bulkStatusDialog = true;
    },
    onBulkStatusConfirmed({ notify, reason }) {
      const targetStatus = this.pendingBulkStatus;
      const alreadyThere =
        targetStatus === 3
          ? this.$t("already_suspended")
          : this.$t("already_active");

      this.executeBulk(this.selected.slice(), {
        title: targetStatus === 3 ? this.$t("suspend_selected") : this.$t("activate_selected"),
        label: (user) => user.name,
        //still skipped, to save a pointless request rather than for correctness
        skip: (user) => (user.status_id === targetStatus ? alreadyThere : null),
        request: (user) =>
          axios.post("/users/set-status", {
            user_id: user.id,
            status_id: targetStatus,
            notify: notify,
            reason: reason,
          }),
        onSuccess: (user) => {
          user.status_id = targetStatus;
        },
        onFinish: () => this.$emit("bulk-finished"),
      });
    },
    // Approve or reject a whole review queue at once. A school that has just
    // opened for the year has a page of these and nothing to decide about most
    // of them; one at a time through the detail screen was the only way.
    bulkTakeAction(action) {
      const approving = action === 1;

      this.runBulk({
        items: this.selected,
        title: approving ? this.$t("approve_selected") : this.$t("reject_selected"),
        confirmText: approving
          ? this.$t("confirm_bulk_approve", { count: this.selected.length })
          : this.$t("confirm_bulk_reject", { count: this.selected.length }),
        label: (user) => user.name,
        request: (user) =>
          axios.post("/users/take-action", {
            user_id: user.id,
            reason: approving ? "Approved" : this.$t("rejected_in_bulk"),
            action: action,
          }),
        onFinish: () => this.$emit("bulk-finished"),
      });
    },
    bulkDeleteUsers() {
      this.runBulk({
        items: this.selected,
        title: this.$t("delete_selected"),
        confirmText: this.$t("confirm_bulk_delete_users", { count: this.selected.length }),
        label: (user) => user.name,
        request: (user) => axios.post("/users/delete-user", { user_id: user.id }),
        onFinish: () => this.$emit("bulk-finished"),
      });
    },
    bulkDeleteSchools() {
      this.runBulk({
        items: this.selected,
        title: this.$t("delete_selected"),
        confirmText: this.$t("confirm_bulk_delete_schools", { count: this.selected.length }),
        label: (school) => school.name,
        request: (school) =>
          axios.delete("/users/delete-school", { data: { school_id: school.id } }),
        onFinish: () => this.$emit("bulk-finished"),
      });
    },
    getStatusColor(status)
    {
      return this.statuses[status-1].color;
    },
    getStatusValue(status)
    {
      return this.translatedStatuses[status-1].value;
    },
    getBusAssignmentColor(bus)
    {
        return bus != null? 'info': 'error';
    },
    //the bus a student rides, or null when they have none. A student has no
    //settings row at all until they are first mapped, so the row itself has
    //to be allowed to be missing
    studentBus(student, morning)
    {
      const settings = student ? student.student_settings : null;
      if (!settings) return null;
      return (morning ? settings.morning_bus : settings.afternoon_bus) || null;
    },
    getHeader()
    {
      return this.applySortability(this.headersForType());
    },
    headersForType()
    {
      if(this.userType === 'drivers') return this.translatedDriverHeaders;
      if(this.userType === 'students')
      {
            if(this.mode === 'simple'){
                return this.translatedStudentSimpleHeaders;
            }else{
                return this.translatedStudentAdvancedHeaders;
            }
      }
      if(this.userType === 'guardians') return this.translatedParentHeaders;
      if(this.userType === 'schools') return this.translatedSchoolHeaders;
      return this.translatedDefaultsHeaders;
    },
    //students have no login of their own, so there is no password to reset
    canResetPassword(item)
    {
        return item.email != null && item.email !== '' && item.role_id !== 6;
    },
    checkIfLocationEnabled(item)
    {
        return this.userType === 'students' && item.student_settings != null && (item.student_settings.pickup_lat != null && item.student_settings.pickup_lng != null || item.student_settings.drop_off_lat != null && item.student_settings.drop_off_lng != null);
    }
  },
};
</script>
<style lang="scss">
.theme--light.v-list-item:not(.v-list-item--active):not(.v-list-item--disabled):hover{
  cursor: pointer;
  background: rgba($primary-shade--light, 0.15) !important;
}
</style>
